import { type ReactNode } from 'react'

import { Space, Text, WithEllipsisTooltip } from '@procurify/ui'

import { Price } from '@webapp/core/components/Price'
import { type CurrencyName } from '@webapp/core/entities/external/Currency/CurrencyName'
import { useFormatStaticAsset } from '@webapp/core/hooks/useFormatStaticAsset'
import { useProcurifyIntl } from '@webapp/hooks'
import { type IConversationOrderSummaryDeliveryItem } from './ConversationOrderSummaryDelivery'
import { ConversationOrderSummaryThumbnail } from './ConversationOrderSummaryThumbnail'
import messages from './messages'
import { ConversationMarkdown } from '../ConversationMarkdown'
import { ConversationOrderSummaryDeliveryLineStyled } from './styles/ConversationOrderSummaryDeliveryLineStyled'
import { ConversationOrderSummaryDeliveryLogoStyled } from './styles/ConversationOrderSummaryDeliveryLogoStyled'
import { ConversationOrderSummaryPriceStyled } from './styles/ConversationOrderSummaryPriceStyled'
import { ConversationOrderSummaryValueStyled } from './styles/ConversationOrderSummaryValueStyled'

export interface IConversationOrderSummaryDeliveryLineProps {
  item: IConversationOrderSummaryDeliveryItem
  currency: CurrencyName
  quantity: ReactNode
  isCentered?: boolean
}

export const ConversationOrderSummaryDeliveryLine = ({
  item,
  currency,
  quantity,
  isCentered,
}: IConversationOrderSummaryDeliveryLineProps) => {
  const intl = useProcurifyIntl()
  const formatStaticAsset = useFormatStaticAsset()
  const lineCurrency = item.currency ?? currency

  const renderDelivery = () => {
    if (!item.delivery) return null
    return (
      <Space direction='horizontal' size='small' align='center'>
        {item.delivery.logoUrl && (
          <ConversationOrderSummaryDeliveryLogoStyled
            src={formatStaticAsset(item.delivery.logoUrl)}
            alt=''
          />
        )}
        <Text inline color='textSecondary'>
          <ConversationMarkdown content={item.delivery.label} isInline />
        </Text>
      </Space>
    )
  }

  return (
    <ConversationOrderSummaryDeliveryLineStyled>
      <Space
        direction='horizontal'
        size='medium'
        align={isCentered ? 'center' : 'start'}
      >
        <ConversationOrderSummaryThumbnail img={item.img} />
        <Space.Item grow={1} minWidth={0}>
          <WithEllipsisTooltip title={item.name}>
            <ConversationOrderSummaryValueStyled
              $isBold
              $isTruncated
              className='fs-mask'
            >
              {item.name}
            </ConversationOrderSummaryValueStyled>
          </WithEllipsisTooltip>
          <Text color='textSecondary' className='fs-mask'>
            {intl.formatMessage(messages.unitPrice, {
              price: (
                <Price
                  key='unit-price'
                  inline
                  fontWeight='normal'
                  price={item.price}
                  currency={lineCurrency}
                />
              ),
            })}
          </Text>
          {renderDelivery()}
        </Space.Item>
        <Space.Item>{quantity}</Space.Item>
        <ConversationOrderSummaryPriceStyled className='fs-mask'>
          <ConversationOrderSummaryValueStyled $isBold>
            <Price
              inline
              price={item.price * item.quantity}
              currency={lineCurrency}
            />
          </ConversationOrderSummaryValueStyled>
        </ConversationOrderSummaryPriceStyled>
      </Space>
    </ConversationOrderSummaryDeliveryLineStyled>
  )
}
